/* eslint-disable @typescript-eslint/no-unused-vars */
import { v4 } from "uuid";
import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SpeechRecognition from "react-speech-recognition";
import { getAccessTokenAPI } from "../../api/config/getAccessToken";
import { getItinerariesListAPI } from "../../api/itinerary/getSaveItinerariesList";
import { ERR_NETWORK, chatInteractionAPI } from "../../api/llm/chatInteractionAPI";
import { getItineraryImageURL } from "../../api/llm/getItineraryImageURL";
import { ROUTES } from "../../constant";
import { DialogSelected, ENVIRONMENT_TYPE, INVALID_JSON_PROMPT, LLM_RESPONSE_ERROR } from "../../constant/common";
import { SELECTED_CHAT_DATA } from "../../constant/dummyData";
import { STREAMING_ENABLE, environment } from "../../constant/environment";
import { Messages } from "../../constant/messages/toastMessages";
import { ScreenTypes, localStorageName } from "../../enums";
import { toastMessage } from "../../helpers/toast/toastMessage";
import useCustomErrorHandler from "../../hooks/useCustomErrorHandler";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { setChatId } from "../../utils/chat/chatId";
import { iChatData } from "../../types";
import { checkIfThereIsDestination, scrollToBottom } from "../../utils";
import { appendErrorMessageTochat } from "../../utils/appendErrorMessageToChat";
import { consoleLog } from "../../utils/consoleLog/consoleLog";
import { checkIfRateLimitExceed, updateRateLimitCount } from "../../utils/rateLimit/rateLimit";
import { useDataStreamingContext } from "../dataStreamingContext/DataStreamingContext";
import { iDataStreamingContext } from "../dataStreamingContext/dataStreamingContextInterface";
import { StateContext } from "../globalContext/context";
import { iGlobalContext } from "../globalContext/interface";
import { useHistoryContext } from "../historyContext/historyContext";
import { iHistoryContext } from "../historyContext/interface";
import { iSocketContext, useSocketContext } from "../socketContext/SocketContext";
import { iRecommendedTripContext } from "./recommendedTripContextInterface";
import { usePixelEvent } from "../../hooks/usePixelEvent";
import { useNavigationUrl } from "../../hooks/useNavigationUrl";

const RecommendedTripContext = createContext({} as iRecommendedTripContext);

export const RecommendedTripContextProvider = ({ children }: { children: ReactNode }) => {
  const context = useContext(StateContext);
  const {
    chatInputText,
    setChatInputText,
    setShowMoreFlag,
    setOpenOverlayScreen,
    setChatData,
    setSharedItineraryId,
    setIsItinerarySavedFlag,
    setenableMultiSelect,
    setScreen,
    setFirstScreen,
    openDetailsSliderFlag,
    setSelectedChatData,
    isMobile,
    chatData,
    userInfo,
    appConfig,
    accessJWT,
    setAccessJWT,
    setIsValidAccessToken,
    setLoginCreateAccountState,
    setSignInClick,
    userLocation,
  }: iGlobalContext = context;
  const { sendPixelEvent } = usePixelEvent();

  const { setCallSaveHistoryAPIFlag, setCurrentHistoryId }: iHistoryContext = useHistoryContext();
  const {
    disableInput,
    setDisableInput,
    chatAbortController,
    chatAbortControllerRef,
    startSocketStreamingTimer,
    setRegeneratingItinerary,
  }: iDataStreamingContext = useDataStreamingContext();
  const { handleCloseSocketConnection, handleOpenSocketConnection, setStreamingFlagAndResponseType }: iSocketContext =
    useSocketContext();

  const { handleCustomError } = useCustomErrorHandler();
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [temFreqViewTrips, setFreqViewTrips] = useSessionStorage(
    localStorageName.TriptroveFrequentlyViewTrips,
    [] as any
  );
  const [index, setIndex] = useState({
    first: 0,
    last: 3,
  });
  useEffect(() => {
    setIndex({
      first: 0,
      last: temFreqViewTrips.length - 1,
    });
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 5320,
      });
    }
  }, [temFreqViewTrips]);

  const [frequentlyViewTrips, setFrequentlyViewTrips] = useState([] as any[]);

  const [urlParamQuestion, setURLParamQuestion] = useState("");
  const { pathname } = useLocation();
  /**
   * Side effect to listen if user has added the question in the URL
   */
  useEffect(() => {
    try {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const data = urlParams.get("prompt");
      if (data && !pathname.startsWith(ROUTES.onBoarding)) {
        const formattedData = decodeURIComponent(data)?.replace(/\+/g, " ").replace(/=/g, "").replace(/\?/g, "");
        if (formattedData?.toLocaleLowerCase() === "none") return;
        setURLParamQuestion(formattedData);
        setChatInputText(formattedData);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function getAccessToken() {
    let response = await getAccessTokenAPI();
    if (response?.statusCode === 200 && response?.data) {
      setAccessJWT(response?.data);
      setIsValidAccessToken(true);
      return response?.data;
    } else {
      response = await getAccessTokenAPI();
      if (response?.statusCode === 200 && response?.data) {
        setAccessJWT(response?.data);
        setIsValidAccessToken(true);
        return response?.data;
      }
    }
    return "";
  }

  async function callChatAPIUsingURLParamPrompt() {
    if (urlParamQuestion) {
      // sendPixelEvent("interactedWithChat", { event: "home-chat-send-llm-from-url-param" });
      sendPixelEvent("startChat", { event: "home-chat-send-llm-from-url-param" });
      consoleLog(["PIXEL EVENT SESSION START"]);
      setChatInputText(urlParamQuestion);
      if (chatInputText) {
        setChatData([
          {
            id: v4(),
            message: urlParamQuestion,
            isUserMessage: true,
            prompt: [],
            isLoading: false,
            destinationList: [],
            singleSelectedDestination: false,
          },
        ]);
        const token = await getAccessToken();
        await handleCreateItineraryStreaming();
      }
    }
  }
  useEffect(() => {
    callChatAPIUsingURLParamPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParamQuestion]);
  const { navigateWithParams } = useNavigationUrl();

  async function handleCreateItineraryStreaming(prompt?: string) {
    // triggerGAEvent(GA_LABEL_ENUMS.chat_input_field_send_prompt_to_llm_click);
    consoleLog(["PROMPT", prompt]);
    setSignInClick(true);
    const rateLimitExceed = checkIfRateLimitExceed(userInfo?.loggedIn ?? false, appConfig);
    if (rateLimitExceed) {
      setLoginCreateAccountState({
        page: DialogSelected.LOGIN,
        flag: true,
        loginRequired: true,
      });
      return;
    }
    scrollToBottom(scrollContainerRef);
    setenableMultiSelect(false);
    if (!prompt && chatInputText === "") {
      toastMessage.info("Please type your message!");
      return;
    }
    setOpenOverlayScreen(true);
    setDisableInput(true);
    SpeechRecognition.stopListening();
    setShowMoreFlag(false);
    chatAbortControllerRef.current = chatAbortController;
    setSelectedChatData(SELECTED_CHAT_DATA);
    setChatData([
      {
        id: v4(),
        message: prompt ?? chatInputText,
        isUserMessage: true,
        prompt: [],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
        shouldNotShowFirstMessage: prompt ? true : false,
      },
      {
        id: v4(),
        message: "",
        isUserMessage: false,
        prompt: [],
        isLoading: true,
        destinationList: [],
        singleSelectedDestination: false,
        isCompleteMessage: false,
      },
    ]);
    const chatId = v4();
    setChatId(chatId);
    setCurrentHistoryId("");
    // Pill-prompt path: the text-input send (`handleNewTripSendMessage` in
    // pocContext) puts `accessToken: accessJWT` on the WS body — backend uses
    // it to resolve the signed-in user for the LLM turn. Pills must match or
    // the request reaches the LLM with no auth context and degrades to the
    // anonymous path. Keep this in lockstep with pocContext if either body
    // shape changes.
    handleOpenSocketConnection({
      userId: userInfo?.id ?? null,
      chatId: chatId,
      messages: [],
      iteneryInput: null,
      userInput: prompt ?? chatInputText,
      userLocation,
      accessToken: accessJWT,
    });
    setRegeneratingItinerary(false);
    startSocketStreamingTimer();
  }

  async function handleCreateItinerary(paramToken: string) {
    setSignInClick(true);
    const rateLimitExceed = checkIfRateLimitExceed(userInfo?.loggedIn ?? false, appConfig);
    if (rateLimitExceed) {
      setLoginCreateAccountState({
        page: DialogSelected.LOGIN,
        flag: true,
      });
      return;
    }

    scrollToBottom(scrollContainerRef);
    setenableMultiSelect(false);
    if (chatInputText === "") {
      toastMessage.info("Please type your message!");
      return;
    }
    setOpenOverlayScreen(true);
    setDisableInput(true);
    SpeechRecognition.stopListening();
    setShowMoreFlag(false);
    const abortController = new AbortController();
    chatAbortControllerRef.current = abortController;
    let response = await chatInteractionAPI(
      paramToken,
      userInfo?.id || "",
      chatInputText,
      [],
      abortController.signal,
      null,
      appConfig
    );
    scrollToBottom(scrollContainerRef);
    if (abortController.signal.aborted) {
      setChatData((prev: iChatData[]) => {
        const length = prev.length;
        if (prev.length > chatData.length) return [...prev.slice(0, length - 2)];
        return prev;
      });
      return;
    }

    /**
     * Recalling the API if it fails
     */
    if (checkIfThereIsDestination(response) === LLM_RESPONSE_ERROR.jsonError.error) {
      response = await chatInteractionAPI(
        paramToken,
        userInfo?.id || "",
        chatInputText + INVALID_JSON_PROMPT,
        [],
        abortController.signal,
        null,
        appConfig
      );
    }

    if (response === ERR_NETWORK) {
      setDisableInput(false);
      setChatData([
        {
          id: v4(),
          message: chatInputText,
          isUserMessage: true,
          prompt: [],
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        {
          id: v4(),
          isErrorMessage: true,
          message: LLM_RESPONSE_ERROR.timeOut.message,
          isUserMessage: false,
          prompt: [],
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
      ]);

      setOpenOverlayScreen(false);
      setDisableInput(false);
      setScreen("");
      setCallSaveHistoryAPIFlag({
        flag: true,
        method: "POST",
      });
      if (isMobile) navigateWithParams(ROUTES.TopDestination);
      else setFirstScreen(ScreenTypes.topDestination);
      setChatInputText("");
      return;
    }

    if (response === null || (!response && !abortController.signal.aborted) || !response?.data?.result) {
      setChatData([
        {
          id: v4(),
          message: chatInputText,
          isUserMessage: true,
          prompt: [],
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
      ]);
      appendErrorMessageTochat(chatData, setChatData, response?.message ?? LLM_RESPONSE_ERROR.jsonError.message);

      setOpenOverlayScreen(false);
      setDisableInput(false);
      setScreen("");
      setCallSaveHistoryAPIFlag({
        flag: true,
        method: "POST",
      });
      if (isMobile) navigateWithParams(ROUTES.TopDestination);
      else setFirstScreen(ScreenTypes.topDestination);
      setChatInputText("");
      return;
    }
    setOpenOverlayScreen(false);

    response = checkIfThereIsDestination(response);
    if (response === LLM_RESPONSE_ERROR.jsonError.error) {
      setDisableInput(false);
      if (environment !== ENVIRONMENT_TYPE.PRODUCTION) {
        toastMessage.error(
          Messages["SaveItinerary.destinationError"].messageTitle,
          Messages["SaveItinerary.destinationError"].message
        );
      }
      setChatData([
        {
          id: v4(),
          message: chatInputText,
          isUserMessage: true,
          prompt: [],
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        {
          isErrorMessage: true,
          id: v4(),
          message: LLM_RESPONSE_ERROR.jsonError.message,
          isUserMessage: false,
          prompt: [],
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
      ]);

      setOpenOverlayScreen(false);
      setDisableInput(false);
      setScreen("");
      setCallSaveHistoryAPIFlag({
        flag: true,
        method: "POST",
      });
      if (isMobile) navigateWithParams(ROUTES.TopDestination);
      else setFirstScreen(ScreenTypes.topDestination);
      setChatInputText("");
      return;
    }
    const citiesList = response?.data?.cities;
    if (citiesList?.length > 0) {
      const tempResponse = citiesList.map((ele: any) => ({
        ...ele,
        id: v4(),
        checkFlag: false,
        locations: [],
      }));
      setChatData([
        {
          id: v4(),
          message: chatInputText,
          isUserMessage: true,
          prompt: [],
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        {
          ...response?.data,
          results: response?.data?.results,
          cities: [],
          city_wise_itinerary: [],
          result: "",
          messages: [],
          fromCity: response?.data?.from,
          noOfDays: response?.data?.numberOfdays,
          id: v4(),
          message: "",
          preText: response?.data?.preText,
          postText: response?.data?.postText,
          prompt: response?.data?.messages,
          isUserMessage: false,
          isLoading: false,
          singleSelectedDestination: response?.data?.singleCity,
          destinationList: tempResponse,
          itinerary_version: "1",
          complete_itinerary_description: response?.data?.complete_itinerary_description,
        },
      ]);
      const currentItineraryList = tempResponse;
      for (let i = 0; i < currentItineraryList.length; i++) {
        const cityName =
          currentItineraryList[i]?.destination_city ||
          currentItineraryList[i]?.city_name ||
          currentItineraryList[i]?.destination_city ||
          "";
        let imageRespone: any = await getItineraryImageURL(paramToken, cityName);
        let count = 0;
        while (!imageRespone || !imageRespone?.data?.image) {
          // Limiting the number of API call to 10 times incase of 409 error
          count++;
          if (count === 3) break;
          // re calling the API
          imageRespone = await getItineraryImageURL(paramToken, cityName);
        }
        if (imageRespone?.statusCode === 200) {
          currentItineraryList[i].imageURL = imageRespone.data.image;
        } else {
          currentItineraryList[i].imageURL = "https://error.error";
        }
      }
    } else {
      setChatData([
        {
          id: v4(),
          message: chatInputText,
          isUserMessage: true,
          prompt: [],
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        {
          id: v4(),
          message: response?.data?.result,
          prompt: response?.data?.messages,
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
      ]);
    }

    setOpenOverlayScreen(false);
    setDisableInput(false);
    setScreen("");
    setCallSaveHistoryAPIFlag({
      flag: true,
      method: "POST",
    });
    if (isMobile) navigateWithParams(ROUTES.TopDestination);
    else setFirstScreen(ScreenTypes.topDestination);
    setChatInputText("");
    scrollToBottom(scrollContainerRef);
    updateRateLimitCount();
  }

  const [, setSelectedRecommendedTripId] = useState("");

  function handleViewItineraryDetails(data: any) {
    setSelectedChatData(
      typeof data?.metaData === "string"
        ? JSON.parse(data?.metaData)[0].selectedChatData
        : data?.metaData.selectedChatData
    );
    setSelectedRecommendedTripId(data.id);
    setSharedItineraryId("");
    setIsItinerarySavedFlag(false);
    if (isMobile) navigateWithParams(ROUTES.RecommendedTripDetails);
    setScreen(ScreenTypes.SavedItinerary);
  }

  useEffect(() => {
    if (openDetailsSliderFlag === false) setSelectedRecommendedTripId("");
  }, [openDetailsSliderFlag]);

  const handleCancelStreaming = () => {
    handleCloseSocketConnection();
    if (isMobile) navigateWithParams(ROUTES.TopDestination);
    else {
      setFirstScreen(ScreenTypes.topDestination);
      setScreen("");
    }
    const promptContext: any = [
      {
        role: "user",
        content: chatData[0]?.message,
      },
    ];
    setChatData((prev: iChatData[]) => {
      if (prev.length === 0) return prev;
      return [
        ...prev.slice(0, -1),
        {
          id: v4(),
          message: "",
          prompt: promptContext,
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
          isProcessInterrupted: true,
        },
      ];
    });
    setStreamingFlagAndResponseType({
      isStillStreamingFlag: false,
      chatId: "",
      responseType: "",
      showMoreClickFlag: false,
    });
  };

  const handleCancel = () => {
    if (chatAbortControllerRef.current) {
      chatAbortControllerRef.current.abort();
      setOpenOverlayScreen(false);
      setChatInputText("");
      setDisableInput(false);
    }
  };
  function handleScrollLeft() {
    if (index.first > 0 && sliderRef.current) {
      sliderRef.current.scrollBy({
        left: -190,
        behavior: "smooth",
      });
      setIndex({
        first: index.first - 1,
        last: (index.last - 1 + temFreqViewTrips.length) % temFreqViewTrips.length,
      });
    }
  }

  function handleScrollRight() {
    setFrequentlyViewTrips([...frequentlyViewTrips, temFreqViewTrips[index.first]]);
    setIndex({
      first: (index.first + 1) % temFreqViewTrips.length,
      last: (index.last + 1) % temFreqViewTrips.length,
    });
    if (sliderRef.current) {
      sliderRef.current.scrollBy({
        left: 190,
        behavior: "smooth",
      });
    }
  }

  /**
   * Upcoming trip
   */
  const [upcomingTripArr, setUpcomingTripArr] = useSessionStorage("_trip_trove_upcoming_trip_arr", []);

  async function getUpcomingTrip() {
    try {
      const response: any = await getItinerariesListAPI(accessJWT, userInfo?.id, "upcoming", 0, 10);

      if (response?.statusCode === 200) {
        setUpcomingTripArr(response?.data || []);
      } else {
        handleCustomError(response?.statusCode, "itinerary.list");
        return null;
      }
    } catch (error) {
      console.error("Error fetching upcoming trips:", error);
    }
  }

  // useEffect(() => {
  //   getUpcomingTrip();
  // }, [userInfo]);

  const value = useMemo(
    () => ({
      scrollContainerRef,
      frequentlyViewTrips,
      index,
      handleScrollLeft,
      sliderRef,
      handleViewItineraryDetails,
      handleScrollRight,
      disableInput,
      handleCreateItinerary: STREAMING_ENABLE ? handleCreateItineraryStreaming : handleCreateItinerary,
      handleCancel: STREAMING_ENABLE ? handleCancelStreaming : handleCancel,
      setDisableInput,
      upcomingTripArr,
      setUpcomingTripArr,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      scrollContainerRef,
      frequentlyViewTrips,
      index,
      disableInput,
      STREAMING_ENABLE,
      handleCreateItineraryStreaming,
      handleCreateItinerary,
      handleCancelStreaming,
      handleCancel,
      upcomingTripArr,
      setUpcomingTripArr,
    ]
  );
  return <RecommendedTripContext.Provider value={value}>{children}</RecommendedTripContext.Provider>;
};
export const useRecommendedTripContext = () => useContext(RecommendedTripContext);
